import React from 'react';
import { MapPin, Users } from 'lucide-react';

interface CalendarDescriptionProps {
  description?: string | null;
  physicalAddress?: string | null;
  addressVisibility?: 'public' | 'subscribers' | 'private';
  isSubscribed?: boolean;
  subscriptionLimit?: number | null;
  subscriberCount?: number;
}

export default function CalendarDescription({ 
  description, 
  physicalAddress,
  addressVisibility = 'subscribers',
  isSubscribed = false,
  subscriptionLimit,
  subscriberCount = 0
}: CalendarDescriptionProps) {
  // Determine if address should be shown
  const showAddress = addressVisibility === 'public' || 
    (addressVisibility === 'subscribers' && isSubscribed) ||
    addressVisibility === 'private';

  // Create Google Maps URL
  const getGoogleMapsUrl = (address: string) => {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  if (!description && !physicalAddress && !subscriptionLimit) return null;

  // Calculate remaining spots
  const remainingSpots = subscriptionLimit ? subscriptionLimit - subscriberCount : null;
  const isAlmostFull = remainingSpots !== null && remainingSpots <= Math.ceil(subscriptionLimit * 0.1); // 10% or less spots remaining

  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 space-y-4 max-w-2xl mx-auto">
      {description && (
        <p className="text-lg leading-relaxed whitespace-pre-line text-center">
          {description}
        </p>
      )}
      
      {physicalAddress && showAddress && (
        <div className="flex items-center justify-center">
          <MapPin className="h-5 w-5 mr-3 flex-shrink-0" />
          <a 
            href={getGoogleMapsUrl(physicalAddress)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-lg leading-relaxed hover:underline text-center"
          >
            {physicalAddress}
          </a>
        </div>
      )}

      {subscriptionLimit && (
        <div className="flex items-center justify-center text-lg">
          <Users className="h-5 w-5 mr-3 flex-shrink-0" />
          {isAlmostFull ? (
            <span className="animate-pulse text-amber-200">
              Only {remainingSpots} {remainingSpots === 1 ? 'spot' : 'spots'} remaining!
            </span>
          ) : remainingSpots === 0 ? (
            <span className="text-red-200">
              All spots taken - Join the waitlist
            </span>
          ) : subscriberCount > 0 ? (
            <span>
              Join {subscriberCount} others - Limited to {subscriptionLimit} subscribers
            </span>
          ) : (
            <span>
              Limited to {subscriptionLimit} subscribers
            </span>
          )}
        </div>
      )}
    </div>
  );
}